/**
 * Prestige screen - Prestige 1 & 2 panels + upgrade shops
 */

import { PRESTIGE_UPGRADES, getPrestigeUpgradeCost, PRESTIGE2_UPGRADES } from '../game/data/prestige.js';
import {
  calculateGcEarned, canPrestige, calculateCoresEarned, canPrestige2,
} from '../game/systems/prestige.js';
import { formatNumber } from '../utils/format.js';
import { t } from '../i18n/index.js';
import { playSfx } from '../sound.js';
import {
  dispatch, getState, getViewState, setViewState, onRender, showToast, setInnerHTML, showPrestigeConfirm,
} from './renderer.js';
import type { PrestigeUpgradeId, Prestige2UpgradeId } from '../game/state.js';
import { resetConquestCache } from './dominationScreen.js';

export function initPrestigeScreen() {
  onRender(renderPrestigeScreen, { alwaysTabs: ['prestige'] });
}

function renderPrestigeScreen() {
  if (getViewState().currentTab !== 'prestige') return;

  const container = document.getElementById('screen-content');
  if (!container) return;

  const vs = getViewState();
  const state = getState();

  const shellHtml = `
    <div class="prestige-screen">
      <div class="sub-tabs">
        <button class="sub-tab ${vs.prestigeSubTab === 'p1' ? 'active' : ''}" data-psub="p1">${t('prestige_title')}</button>
        <button class="sub-tab ${vs.prestigeSubTab === 'p2' ? 'active' : ''}" data-psub="p2">${t('prestige2_title')}</button>
      </div>
      <div id="prestige-content"></div>
    </div>
  `;

  if (setInnerHTML(container, shellHtml)) {
    container.querySelectorAll('[data-psub]').forEach(btn => {
      btn.addEventListener('click', () => {
        setViewState({ prestigeSubTab: (btn as HTMLElement).dataset.psub as 'p1' | 'p2' });
      });
    });
  }

  if (vs.prestigeSubTab === 'p1') {
    renderPrestige1(state);
  } else {
    renderPrestige2(state);
  }
}

function renderPrestige1(state: ReturnType<typeof getState>) {
  const content = document.getElementById('prestige-content');
  if (!content) return;

  const canP = canPrestige(state);
  const gcGainBase = calculateGcEarned(state.totalIpThisRun, state);
  const hasAmplifier = state.prestigeBoostActive;
  const gcGain = hasAmplifier ? gcGainBase * 2 : gcGainBase;

  let upgradeHtml = '';
  for (const def of PRESTIGE_UPGRADES) {
    const lv = state.prestigeUpgrades[def.id as PrestigeUpgradeId] ?? 0;
    const maxed = lv >= def.maxLevel;
    const cost = maxed ? 0 : getPrestigeUpgradeCost(def, lv);
    const canBuy = !maxed && state.gc >= cost;

    upgradeHtml += `
      <div class="prestige-upgrade-card ${maxed ? 'maxed' : ''}">
        <div class="upgrade-info">
          <div class="upgrade-name">${t(def.nameKey)} ${t('level')}${lv}/${def.maxLevel}</div>
          <div class="upgrade-desc">${t(def.descKey)}</div>
        </div>
        <div style="text-align:right;">
          ${!maxed ? `
            <div class="upgrade-cost" style="color:var(--gc-color)">${cost} \u{1F48E}</div>
            <button class="buy-btn" ${canBuy ? '' : 'disabled'} data-buy-pup="${def.id}">${t('buy')}</button>
          ` : `<span style="color:var(--accent)">${t('max')}</span>`}
        </div>
      </div>
    `;
  }

  const contentHtml = `
    <div class="prestige-panel">
      <div class="prestige-title">${t('prestige_title')}</div>
      <div class="prestige-info-row">
        <span class="prestige-info-label">${t('prestige_total_ip')}</span>
        <span class="prestige-info-value">${formatNumber(state.totalIpThisRun)}</span>
      </div>
      <div class="prestige-info-row">
        <span class="prestige-info-label">${t('prestige_gc_gain')}</span>
        <span class="prestige-info-value" style="color:var(--gc-color)">+${gcGain}${hasAmplifier ? ` <span style="color:#FFD700;font-size:0.85em">(${t('doc_prestige_boost')} x2)</span>` : ''}</span>
      </div>
      <div class="prestige-info-row">
        <span class="prestige-info-label">${t('prestige_gc_current')}</span>
        <span class="prestige-info-value" style="color:var(--gc-color)">${formatNumber(state.gc)}</span>
      </div>
      <button class="prestige-btn" id="prestige1-btn" ${canP ? '' : 'disabled'}>
        ${t('prestige_execute')}
      </button>
      ${!canP ? `<div class="prestige-requirement">${t('prestige_requirement')}</div>` : ''}
    </div>
    <div class="prestige-upgrade-list">${upgradeHtml}</div>
  `;

  if (setInnerHTML(content, contentHtml)) {
    // Prestige button handler
    const pBtn = document.getElementById('prestige1-btn');
    if (pBtn && canP) {
      pBtn.addEventListener('click', () => {
        showPrestigeConfirm(
          t('prestige_execute'),
          `+${gcGain} \u{1F48E}${hasAmplifier ? ' (x2)' : ''}`,
          t('prestige_confirm').replace('{gc}', String(gcGain)),
          () => {
            dispatch({ type: 'PRESTIGE_1' });
            setViewState({ conquestSelectedCountry: null });
            resetConquestCache();
            // Restart game loops to reset tracking variables (prevConqueredSet, etc.)
            if (typeof (window as any).__restartGameLoop === 'function') {
              (window as any).__restartGameLoop();
            }
            playSfx('prestige');
            showToast(`+${gcGain} \u{1F48E}!`);
          },
        );
      });
    }

    // Upgrade buy handlers
    content.querySelectorAll('[data-buy-pup]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = (btn as HTMLElement).dataset.buyPup! as PrestigeUpgradeId;
        dispatch({ type: 'BUY_PRESTIGE_UPGRADE', payload: { upgradeId: id } });
        playSfx('upgrade');
      });
    });
  }
}

function renderPrestige2(state: ReturnType<typeof getState>) {
  const content = document.getElementById('prestige-content');
  if (!content) return;

  const canP2 = canPrestige2(state);
  const coresGain = canP2 ? calculateCoresEarned(state.totalGcEarned, state) : 0;

  let upgradeHtml = '';
  for (const def of PRESTIGE2_UPGRADES) {
    const lv = state.prestige2Upgrades[def.id as Prestige2UpgradeId];
    const maxed = lv >= def.maxLevel;
    const canBuy = !maxed && state.cores >= def.cost;

    upgradeHtml += `
      <div class="prestige-upgrade-card ${maxed ? 'maxed' : ''}">
        <div class="upgrade-info">
          <div class="upgrade-name">${t(def.nameKey)} ${t('level')}${lv}/${def.maxLevel}</div>
          <div class="upgrade-desc">${t(def.descKey)}</div>
        </div>
        <div style="text-align:right;">
          ${!maxed ? `
            <div class="upgrade-cost" style="color:var(--core-color)">${def.cost} \u{1F52E}</div>
            <button class="buy-btn" ${canBuy ? '' : 'disabled'} data-buy-p2up="${def.id}">${t('buy')}</button>
          ` : `<span style="color:var(--accent)">${t('max')}</span>`}
        </div>
      </div>
    `;
  }

  const p2Html = `
    <div class="prestige-panel p2">
      <div class="prestige-title">${t('prestige2_title')}</div>
      <div class="prestige-info-row">
        <span class="prestige-info-label">${t('prestige2_cores_gain')}</span>
        <span class="prestige-info-value" style="color:var(--core-color)">+${coresGain}</span>
      </div>
      <div class="prestige-info-row">
        <span class="prestige-info-label">${t('prestige2_cores_current')}</span>
        <span class="prestige-info-value" style="color:var(--core-color)">${formatNumber(state.cores)}</span>
      </div>
      <button class="prestige-btn p2" id="prestige2-btn" ${canP2 ? '' : 'disabled'}>
        ${t('prestige2_execute')}
      </button>
      ${!canP2 ? `<div class="prestige-requirement">${t('prestige2_requirement')}</div>` : ''}
    </div>
    <div class="prestige-upgrade-list">${upgradeHtml}</div>
  `;

  if (setInnerHTML(content, p2Html)) {
    const p2Btn = document.getElementById('prestige2-btn');
    if (p2Btn && canP2) {
      p2Btn.addEventListener('click', () => {
        showPrestigeConfirm(
          t('prestige2_execute'),
          `+${coresGain} \u{1F52E}`,
          t('prestige2_confirm').replace('{cores}', String(coresGain)),
          () => {
            dispatch({ type: 'PRESTIGE_2' });
            setViewState({ conquestSelectedCountry: null });
            resetConquestCache();
            // Restart game loops to reset tracking variables (prevConqueredSet, etc.)
            if (typeof (window as any).__restartGameLoop === 'function') {
              (window as any).__restartGameLoop();
            }
            playSfx('prestige');
            showToast(`+${coresGain} \u{1F52E}!`);
          },
        );
      });
    }

    content.querySelectorAll('[data-buy-p2up]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = (btn as HTMLElement).dataset.buyP2up! as Prestige2UpgradeId;
        dispatch({ type: 'BUY_PRESTIGE2_UPGRADE', payload: { upgradeId: id } });
        playSfx('upgrade');
      });
    });
  }
}
